import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router'
import { Loader2 } from 'lucide-react'
import { AppSidebar } from '@/components/app-sidebar'
import { SidebarProvider } from '@/components/ui/sidebar'
import {
  getToken,
  getUserProfile,
  setUserProfile,
  setOrganizationsProfile,
  clearToken,
  clearUserProfile,
  clearOrganizationProfile,
  userApi,
  orgApi,
} from '@/lib/api'
import type { UserProfile, OrganisationProfile } from '@/lib/types'
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner'

export default function DashboardLayout() {
  const navigate = useNavigate()
  const [isValidating, setIsValidating] = useState(() => !!getToken() && !getUserProfile())
  const [profile, setProfile] = useState<UserProfile | null>(() => getUserProfile())

  useEffect(() => {
    const token = getToken()
    const storedProfile = getUserProfile()

    if (!token || storedProfile) {
      setIsValidating(false)
      return
    }

    const logout = () => {
      clearToken()
      clearUserProfile()
      clearOrganizationProfile()
      navigate('/login', { replace: true })
    }

    const validate = async () => {
      try {
        const jwtPayload = JSON.parse(atob(token.split('.')[1]))
        const jwtUser = jwtPayload.user as { id: string; role: string; organization_id: string }

        if (!jwtUser?.id) {
          logout()
          return
        }

        const res = await userApi.getUser(jwtUser.id)
        const builtProfile: UserProfile = {
          ...res.data,
          role: jwtUser.role,
          organization_id: jwtUser.organization_id,
        }
        setUserProfile(builtProfile)
        setProfile(builtProfile)

        if (jwtUser.organization_id) {
          const orgRes = await orgApi.getOrganisation(jwtUser.organization_id)
          setOrganizationsProfile([orgRes.data as OrganisationProfile])
        }
      } catch {
        logout()
      } finally {
        setIsValidating(false)
      }
    }

    validate()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (isValidating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-secondary" />
      </div>
    )
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <EmailVerificationBanner profile={profile} dismissible />
        <Outlet />
      </div>
    </SidebarProvider>
  )
}
